/* ファイルアップローダアコーディオン */
import React, { useState, useEffect } from 'react';
import { Collapse, Card } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import FileUpload from './FileUpload';
import { ShelfInfo, Reception } from '../types/FileUpload';
import { init_shelf_state } from '../const/FileUpload';
import heic2any from 'heic2any';
import { network_error_message, http_status_code_list } from '../Const';
import { authErrorTimeout } from '../Common';

interface Props {
  shelf_type: string;
  label: string | JSX.Element;
  is_open: boolean;
  shelf_info: ShelfInfo;
  setLoadingModalShow: (show: boolean) => void;
  setPreviewModalShow: (
    show: boolean,
    image_source: string,
    width: number,
    height: number,
    mime: string
  ) => void;
  reception: Reception;
  setShelfInfo: React.Dispatch<React.SetStateAction<ShelfInfo>>;
  looks_inactive: boolean;
  updateDefaultShelfOpen: (shelf_type: string, open_flag: boolean) => void;
  proposer_cd: string;
}

const FileUploadShelf: React.FC<Props> = ({
  shelf_type,
  label,
  is_open,
  shelf_info,
  setLoadingModalShow,
  setPreviewModalShow,
  reception,
  setShelfInfo,
  looks_inactive,
  updateDefaultShelfOpen,
  proposer_cd,
}) => {
  useEffect(() => {
    setIsAccordionOpen(is_open);
  }, [is_open]);

  const [is_accordion_open, setIsAccordionOpen] = useState(is_open);
  const history = useHistory();

  // アコーディオンを開閉する
  const toggleAccordion = () => updateDefaultShelfOpen(shelf_type, !is_accordion_open);

  /**
   * マスク画面へ遷移
   * @param {string} shelf_cd 棚識別CD
   * @param {number} target_index 対象ファイル識別番号
   */
  const handleClickMask = (shelf_cd: string, target_index: number) => {
    history.push(
      `/reception/${reception.reception_cd}/${reception.reception_id}/${reception.status_code}/mask/${shelf_cd}/${target_index}`
    );
  };

  // 画像を追加時
  const handleChange = async (event: React.ChangeEvent<HTMLInputElement>, num: number) => {
    // ローディング画面開始
    setLoadingModalShow(true);
    event.persist();
    // target.filesがnull、または要素数が0の時は処理を中断する
    if (event.target.files === null || event.target.files.length === 0) {
      return;
    }

    // ファイル名取得
    const file_name = event.target.files[0].name;

    let file: Blob;
    // ファイル情報を取得
    if (file_name.toLowerCase().match(/heif$|heic$/)) {
      // heifまたはheicファイルならjpegに変換
      file = (await heic2any({
        blob: event.target.files[0],
        toType: 'image/jpeg',
        quality: 0.1,
      })) as Blob;
    } else {
      file = event.target.files[0];
    }

    // Imageオブジェクト
    const image = new Image();
    if (file.type.match(/^image/)) {
      image.src = URL.createObjectURL(file);
    }

    const reader = new FileReader();
    reader.onload = (e: any) => {
      const new_file_data = shelf_info.file_data.slice();
      new_file_data[num]['file'] = {
        file_name: file_name,
        img: e.target.result,
        file_body: file,
        width: 0,
        height: 0,
        mime: file.type,
      };

      if (file.type.match(/^image/)) {
        image.onload = () => {
          new_file_data[num]['file'].width = image.width;
          new_file_data[num]['file'].height = image.height;
          setShelfInfo(prev => {
            return { ...prev, file_data: new_file_data };
          });
        };
      } else {
        setShelfInfo(prev => {
          return { ...prev, file_data: new_file_data };
        });
      }

      // エラーメッセージをリセットする
      new_file_data[num].errors.document_type = '';
      new_file_data[num].errors.file_required = '';
    };
    reader.readAsDataURL(file);
    // イベント対象の値を初期化
    event.target.value = '';

    // ローディング画面終了
    setLoadingModalShow(false);
  };

  // アップロード処理
  const handleUpload = (num: number) => {
    const imageUpload = async (num: number) => {
      try {
        setLoadingModalShow(true);

        const params = new FormData();
        params.append('file', shelf_info.file_data[num]['file']['file_body'] ?? '');
        params.append('reception_id', reception.reception_id.toString());
        params.append('reception_cd', reception.reception_cd);
        params.append('reception_sub_cd', reception.reception_sub_cd);
        params.append('proposer_cd', proposer_cd);
        params.append('ident_lv1_cd', reception.ident_lv1_cd);
        params.append('ident_lv2_cd', reception.ident_lv2_cd);
        params.append('ident_lv3_cd', reception.ident_lv3_cd);
        params.append('shelf_cd', shelf_info.shelf_cd);
        params.append('document_cd', shelf_info.file_data[num].document_type);
        params.append('doc_no', String(num + 1));
        const response = await axios.post(
          `${location.origin}${process.env.MIX_MOCK_URL}/api/file/upload`,
          params,
          {
            headers: {
              'content-type': 'multipart/form-data',
            },
          }
        );

        // ディープコピー
        const new_file_input = shelf_info.file_data.slice();

        // 対象のオブジェクトをディープコピー
        const new_file_input_object = { ...new_file_input[num] };
        new_file_input_object['is_uploaded'] = true;
        new_file_input_object['document_status'] = response.data.file_status;
        new_file_input_object['submission_date'] = response.data.file_upload_date;
        new_file_input_object['errors']['document_type'] = '';
        new_file_input_object['errors']['file_required'] = '';

        // S3情報をディープコピー
        const new_s3_info = { ...new_file_input_object.s3_info };
        new_s3_info.file_name = response.data.file_name;
        new_file_input_object.s3_info = new_s3_info;

        new_file_input[num] = new_file_input_object;
        setShelfInfo(prev => {
          return { ...prev, file_data: new_file_input };
        });
      } catch (error) {
        if (error.message === network_error_message) {
          // ネットワークエラーなので認証エラーとしてタイムアウト画面に飛ばす
          authErrorTimeout(http_status_code_list.unauthorized);
        }

        const new_file_data = shelf_info.file_data.slice();
        new_file_data[num]['errors']['document_type'] = error.response.data.errors.document_cd;
        new_file_data[num]['errors']['file_required'] = error.response.data.errors.file;
        setShelfInfo(prev => {
          return { ...prev, file_data: new_file_data };
        });
      } finally {
        setLoadingModalShow(false);
      }
    };
    imageUpload(num);
  };

  // 画像を追加ボタン押下時のアクション
  const handleAddInput = (num: number) => {
    // ディープコピー
    const new_file_data = shelf_info.file_data.slice();
    const new_init_shelf_state = {
      ...init_shelf_state,
      document_type: shelf_info.document_type_source[0].key,
    };
    new_init_shelf_state.index = num + 1; // indexを更新
    new_file_data.push(new_init_shelf_state);
    setShelfInfo(prev => {
      return { ...prev, file_data: new_file_data };
    });
  };

  // 削除ボタン押下時のアクション
  const handleDelete = (num: number) => {
    // 画像をアップロード済みの場合は削除処理を行う
    if (shelf_info.file_data[num].is_uploaded) {
      imageDelete(num).then(() => {
        // ディープコピー
        const new_file_data = shelf_info.file_data.filter((file, index) => index !== num);
        setShelfInfo(prev => {
          return { ...prev, file_data: new_file_data };
        });
      });
    } else {
      // ディープコピー
      const new_file_data = shelf_info.file_data.filter((file, index) => index !== num);
      setShelfInfo(prev => {
        return { ...prev, file_data: new_file_data };
      });
    }
  };

  // 取消ボタン押下時のアクション
  const handleCancel = (num: number) => {
    imageDelete(num).then(() => {
      // ディープコピー
      const new_file_data = shelf_info.file_data.slice();
      const new_init_shelf_state = {
        ...init_shelf_state,
        document_type: shelf_info.document_type_source[0].key,
      };
      new_file_data[num] = new_init_shelf_state;
      new_file_data[num].index = num; // indexをセットする
      setShelfInfo(prev => {
        return { ...prev, file_data: new_file_data };
      });
      // Fileフォームにセットされているファイルをリセットする
      const element: HTMLInputElement = document.getElementById('file-input') as HTMLInputElement;
      if (element !== null) {
        element.value = '';
      }
    });
  };
  // 画像の物理削除処理
  const imageDelete = async (num: number) => {
    try {
      setLoadingModalShow(true);

      const params = {
        reception_cd: reception.reception_cd,
        reception_sub_cd: reception.reception_sub_cd,
        document_cd: shelf_info.file_data[num].document_type,
        file_path: shelf_info.file_data[num].s3_info.file_name,
      };
      const response = await axios.post(
        `${location.origin}${process.env.MIX_MOCK_URL}/api/file/delete`,
        params
      );
    } catch (error) {
      if (error.message === network_error_message) {
        // ネットワークエラーなので認証エラーとしてタイムアウト画面に飛ばす
        authErrorTimeout(http_status_code_list.unauthorized);
      }
    } finally {
      setLoadingModalShow(false);
    }
  };

  // 書類提出の種別をstateに保持する
  const handleSelectDocumentType = (num: number, value: string) => {
    // ディープコピー
    const new_file_data = shelf_info.file_data.slice();
    new_file_data[num].document_type = value;
    setShelfInfo(prev => {
      return { ...prev, file_data: new_file_data };
    });
  };

  return (
    <>
      <Card className="shelf px-0 col-12 file-upload">
        <Card.Header
          className={`shelf-header ${is_accordion_open && 'is-open'}`}
          onClick={toggleAccordion}
          aria-expanded={is_accordion_open}
        >
          {label}
          <span className="ac-icon"></span>
        </Card.Header>
        <Collapse className="collapse-body" in={is_accordion_open}>
          <div>
            <Card.Body className="p-2">
              {shelf_info.file_data.map((data, index) => {
                return (
                  <FileUpload
                    key={index}
                    file_input={data}
                    handleChange={handleChange}
                    handleUpload={handleUpload}
                    num={index}
                    handleAddInput={handleAddInput}
                    handleDelete={handleDelete}
                    handleCancel={handleCancel}
                    document_type_source={shelf_info.document_type_source}
                    handleSelectDocumentType={handleSelectDocumentType}
                    max_input={shelf_info.file_data.length}
                    setPreviewModalShow={setPreviewModalShow}
                    can_mask={shelf_info.can_mask}
                    shelf_type={shelf_type}
                    handleClickMask={() => handleClickMask(shelf_info.shelf_cd, index)}
                    looks_inactive={looks_inactive}
                  />
                );
              })}
            </Card.Body>
          </div>
        </Collapse>
      </Card>
    </>
  );
};

export default FileUploadShelf;
